/**
 * Users — who else at this Company may open these settings.
 *
 * An invite creates the account and emails the new person a one-time
 * link to set their own password with. The link goes only to them and is
 * never stored: this login opens the Company's phone number and the key
 * its jobs draft through, and one person typing another's password is
 * not the way to hand that over.
 *
 * Removing takes the account with the row — a login with no Company to
 * administer is not a lesser account, it is a way in with nothing behind
 * it.
 *
 * The two-factor switch is the Company's, and on this page because it is
 * about who can get in. On, everyone logs in with a code from an
 * authenticator app and anyone without one is sent to set it up; the API
 * enforces it, so the switch is not a suggestion to the sign-in page. A
 * lost phone is reset from the person's row by somebody else.
 */
import { useState } from "react";
import { Banner } from "../../components/ui/Banner";
import { Button } from "../../components/ui/Button";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { useSuccessToast } from "../../components/ui/Toast";
import { InlineBanner } from "../../components/ui/InlineBanner";
import { ToggleSettingRow } from "../../components/ui/SettingRow";
import {
  inviteCompanyUser,
  listCompanyUsers,
  removeCompanyUser,
  resetCompanyUserTwoFactor,
  setTwoFactorRequired,
  type CompanyUser,
} from "../../lib/settings-api";
import { useAction } from "../../lib/use-action";
import { useSettingsSource } from "../../lib/use-settings-source";
import { FieldLabel, Loading, SectionHeader } from "./section-chrome";
import styles from "./UsersSection.module.css";

export function UsersSection() {
  const source = useSettingsSource(listCompanyUsers);
  const { loadError } = source;
  const users = source.data?.users ?? null;
  const twoFactorRequired = source.data?.twoFactorRequired ?? false;
  const setUsers = (next: (current: CompanyUser[]) => CompanyUser[]) =>
    source.setData((d) => d && { ...d, users: next(d.users) });
  const setRequired = (next: boolean) =>
    source.setData((d) => d && { ...d, twoFactorRequired: next });
  const [removing, setRemoving] = useState<CompanyUser | null>(null);
  const {
    working: removeWorking,
    error: removeError,
    setError: setRemoveError,
    run: runRemove,
  } = useAction();
  const [resetting, setResetting] = useState<CompanyUser | null>(null);
  const {
    working: resetWorking,
    error: resetError,
    setError: setResetError,
    run: runReset,
  } = useAction();

  // Read back after the switch moves: off has cleared every authenticator
  // app, and on may mean the person who flipped it has none yet, which
  // the read is what finds out.
  const toggleTwoFactor = async (next: boolean) => {
    setRequired(await setTwoFactorRequired(next));
    await source.reload();
  };

  const remove = async () => {
    if (!removing) return;
    await runRemove(async () => {
      await removeCompanyUser(removing.id);
      setUsers((current) =>
        current.filter((u) => u.id !== removing.id),
      );
      setRemoving(null);
    });
  };

  const resetTwoFactor = async () => {
    if (!resetting) return;
    await runReset(async () => {
      await resetCompanyUserTwoFactor(resetting.id);
      setUsers((current) =>
        current.map((u) =>
          u.id === resetting.id ? { ...u, two_factor: false } : u,
        ),
      );
      setResetting(null);
    });
  };

  return (
    <section>
      <SectionHeader
        title="Users"
        description="Everyone who can sign in and change these settings."
      />

      {loadError && (
        <Banner variant="error" className={styles.notice}>
          {loadError}
        </Banner>
      )}

      {!users ? (
        <Loading />
      ) : (
        <>
          <ToggleSettingRow
            id="require-two-factor"
            title="Require an authenticator app"
            description="Everyone logs in with a code from an app such as Google Authenticator."
            checked={twoFactorRequired}
            onChange={toggleTwoFactor}
          />

          <ul className={styles.users}>
            {users.map((user) => (
              <li
                key={user.id}
                className={styles.user}
              >
                <span className={styles.identity}>
                  <span className={styles.email}>
                    {user.email}
                    {user.you && (
                      <span className={styles.you}>
                        you
                      </span>
                    )}
                  </span>
                  {twoFactorRequired && typeof user.two_factor === "boolean" && (
                    <span className={styles.twoFactor}>
                      {user.two_factor
                        ? "Authenticator app set up"
                        : "No authenticator app yet"}
                    </span>
                  )}
                </span>
                {!user.you && (
                  <span className={styles.userActions}>
                    {twoFactorRequired && user.two_factor === true && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setResetting(user)}
                      >
                        Reset two-factor
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setRemoving(user)}
                    >
                      Remove
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>

          {/* The form sits below the list, where the new row will appear. */}
          <InviteForm
            onInvited={(user) =>
              setUsers((current) =>
                [...current, user].sort((a, b) =>
                  a.email.localeCompare(b.email),
                ),
              )
            }
          />
        </>
      )}

      <ConfirmModal
        open={resetting !== null}
        title={`Reset two-factor for ${resetting?.email ?? ""}?`}
        message="Their authenticator app stops working for HireOS. They set up a new one next time they log in."
        confirmLabel="Reset"
        loading={resetWorking}
        error={resetError}
        onConfirm={() => void resetTwoFactor()}
        onCancel={() => {
          setResetting(null);
          setResetError(null);
        }}
      />

      <ConfirmModal
        open={removing !== null}
        title={`Remove ${removing?.email ?? ""}?`}
        message="Their account is deleted and they can no longer sign in. This cannot be undone."
        confirmLabel="Remove"
        loading={removeWorking}
        error={removeError}
        onConfirm={() => void remove()}
        onCancel={() => {
          setRemoving(null);
          setRemoveError(null);
        }}
      />
    </section>
  );
}

function InviteForm({ onInvited }: { onInvited: (user: CompanyUser) => void }) {
  const [email, setEmail] = useState("");
  const { working: busy, error, run } = useAction();
  const toast = useSuccessToast();

  const invite = async () => {
    await run(async () => {
      const address = email.trim();
      onInvited(await inviteCompanyUser(address));
      setEmail("");
      toast(`Invite sent to ${address}.`);
    });
  };

  return (
    <form
      className={styles.inviteForm}
      onSubmit={(e) => {
        e.preventDefault();
        void invite();
      }}
    >
      <FieldLabel htmlFor="invite-email">Add someone</FieldLabel>
      <p className={styles.fieldNote}>
        We email them a link to set their own password.
      </p>
      <div className={styles.inviteRow}>
        <input
          id="invite-email"
          type="email"
          autoComplete="off"
          className={styles.inviteInput}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Button
          size="sm"
          type="submit"
          loading={busy}
          disabled={email.trim() === ""}
        >
          Send invite
        </Button>
      </div>

      {error && <InlineBanner variant="error">{error}</InlineBanner>}
    </form>
  );
}
